/**
 * ImportLibraryModal
 *
 * Super-admin flow for importing a Content Library bundle exported from another
 * environment (typically prod → dev, to seed development games with real
 * content). Four stages, driven by `stage`:
 *
 *   choose  → pick a .zip
 *   upload  → uploading, with progress (bundles run to hundreds of MB)
 *   preview → the dry-run summary; nothing has been written yet
 *   result  → what the committed import actually did
 *
 * The preview stage is the point of the flow: an import is a bulk write into a
 * shared library that can also create placeholder author accounts, so
 * the operator confirms a specific, itemised change rather than discovering it
 * afterwards. Closing before confirming discards the staged upload server-side.
 */

import { useCallback, useRef, useState } from 'react';
import { Modal } from '@swtorbit/ui/react';
import type { ContentImportPreview, ContentImportResult } from '@swtorbit/shared-socket-events';
import {
  commitTrlImport,
  discardTrlImport,
  previewTrlImport,
} from '../transferApi';

interface ImportLibraryModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Called after a committed import so the gallery can reload. */
  onImported: () => void;
}

type Stage = 'choose' | 'upload' | 'preview' | 'importing' | 'result';

/** Human-readable byte size for the chosen file. */
function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  const units = ['KB', 'MB', 'GB'];
  let value = bytes / 1024;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  return `${value.toFixed(value < 10 ? 1 : 0)} ${units[unit]}`;
}

/** A labelled count in the preview summary. */
function SummaryStat({ label, value, tone }: { label: string; value: number; tone?: 'good' | 'muted' }) {
  const valueClass =
    tone === 'good' ? 'text-green-dark' : tone === 'muted' ? 'text-slate-dark' : 'text-white';
  return (
    <div className="d-flex flex-column align-items-center bg-space bg-o-50 border-md border-cyan rounded-sm p-xs">
      <span className={`${valueClass} fw-bold`} style={{ fontSize: '1.6rem', lineHeight: 1.1 }}>
        {value}
      </span>
      <span className="fs-sm text-slate-dark text-uppercase text-center">{label}</span>
    </div>
  );
}

/** A named list (authors) rendered as chips, hidden when empty. */
function NameList({ label, names, tone }: { label: string; names: string[]; tone: 'new' | 'existing' }) {
  if (names.length === 0) return null;
  return (
    <div className="d-flex flex-column gap-2xs">
      <span className="fs-sm text-slate-dark text-uppercase">
        {label} ({names.length})
      </span>
      <div className="d-flex flex-wrap gap-2xs">
        {names.map((name) => (
          <span key={name} className={`btn btn--sm ${tone === 'new' ? 'btn--aqua' : 'btn--outline btn--slate'}`} style={{ cursor: 'default' }}>
            {name}
          </span>
        ))}
      </div>
    </div>
  );
}

export function ImportLibraryModal({ isOpen, onClose, onImported }: ImportLibraryModalProps) {
  const [stage, setStage] = useState<Stage>('choose');
  const [file, setFile] = useState<File | null>(null);
  const [progress, setProgress] = useState(0);
  const [preview, setPreview] = useState<ContentImportPreview | null>(null);
  const [result, setResult] = useState<ContentImportResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [shareAll, setShareAll] = useState(false);
  const abortRef = useRef<AbortController | null>(null);

  const reset = useCallback(() => {
    setStage('choose');
    setFile(null);
    setProgress(0);
    setPreview(null);
    setResult(null);
    setError(null);
    setShareAll(false);
    abortRef.current = null;
  }, []);

  /**
   * Close the modal, discarding an uncommitted staged upload. A staged bundle
   * expires on its own, but releasing the disk immediately is better than
   * leaving hundreds of MB parked for an hour.
   */
  const handleClose = useCallback(() => {
    abortRef.current?.abort();
    if (stage === 'preview' && preview) void discardTrlImport(preview.stagedId);
    reset();
    onClose();
  }, [stage, preview, reset, onClose]);

  const handleFileChange = useCallback((selected: File | null) => {
    setFile(selected);
    setError(null);
  }, []);

  const handleUpload = useCallback(async () => {
    if (!file) return;
    setStage('upload');
    setProgress(0);
    setError(null);
    const controller = new AbortController();
    abortRef.current = controller;
    try {
      const next = await previewTrlImport(file, setProgress, controller.signal);
      setPreview(next);
      setStage('preview');
    } catch (err) {
      if (err instanceof DOMException && err.name === 'AbortError') {
        reset();
        return;
      }
      setError(err instanceof Error ? err.message : 'Failed to read bundle');
      setStage('choose');
    } finally {
      abortRef.current = null;
    }
  }, [file, reset]);

  const handleCommit = useCallback(async () => {
    if (!preview) return;
    setStage('importing');
    setError(null);
    try {
      const next = await commitTrlImport(preview.stagedId, { shareAll });
      setResult(next);
      setStage('result');
      onImported();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to import bundle');
      setStage('preview');
    }
  }, [preview, shareAll, onImported]);

  const handleDone = useCallback(() => {
    reset();
    onClose();
  }, [reset, onClose]);

  return (
    <Modal open={isOpen} onClose={handleClose} title="Import Content Library Bundle">
        <div className="d-flex flex-column gap-sm" style={{ minWidth: 'min(42rem, 90vw)' }}>
          {error && (
            <div className="bg-space bg-o-50 border-md border-red rounded-sm p-xs">
              <span className="text-red fs-sm">{error}</span>
            </div>
          )}

          {/* ── Choose a bundle ─────────────────────────────────────────── */}
          {stage === 'choose' && (
            <>
              <p className="fs-sm text-slate-dark m-0">
                Select a <code>.zip</code> bundle exported from another environment. Screenshots already in
                this library are skipped, so re-importing the same bundle is safe. Planets and authors the
                bundle references are created here if they are missing.
              </p>
              <input
                type="file"
                accept=".zip,application/zip"
                className="input"
                onChange={(e) => handleFileChange(e.target.files?.[0] ?? null)}
              />
              {file && (
                <span className="fs-sm text-slate-dark">
                  {file.name} — {formatBytes(file.size)}
                </span>
              )}
              <div className="d-flex gap-xs justify-content-end">
                <button className="btn btn--outline btn--slate" onClick={handleClose}>Cancel</button>
                <button
                  className="btn btn--fancy btn--green text-uppercase fw-bold"
                  disabled={!file}
                  onClick={() => void handleUpload()}
                >
                  Read Bundle
                </button>
              </div>
            </>
          )}

          {/* ── Uploading ───────────────────────────────────────────────── */}
          {stage === 'upload' && (
            <>
              <p className="fs-sm text-slate-dark m-0">
                Uploading {file?.name}… nothing has been changed yet.
              </p>
              <div className="bg-space bg-o-50 border-md border-cyan rounded-sm" style={{ height: '1.25rem', overflow: 'hidden' }}>
                <div
                  className="bg-aqua-dark"
                  style={{ width: `${Math.round(progress * 100)}%`, height: '100%', transition: 'width 0.2s ease' }}
                />
              </div>
              <span className="fs-sm text-slate-dark">
                {Math.round(progress * 100)}%
                {progress >= 1 ? ' — reading bundle on the server…' : ''}
              </span>
              <div className="d-flex gap-xs justify-content-end">
                <button className="btn btn--outline btn--slate" onClick={() => abortRef.current?.abort()}>Cancel Upload</button>
              </div>
            </>
          )}

          {/* ── Dry-run preview ─────────────────────────────────────────── */}
          {(stage === 'preview' || stage === 'importing') && preview && (
            <>
              <div className="d-flex flex-column gap-2xs">
                <span className="fs-sm text-slate-dark">
                  Exported by <strong className="text-white">{preview.exportedBy}</strong> on{' '}
                  {new Date(preview.exportedAt).toLocaleString()}
                  {preview.appVersion ? ` (app ${preview.appVersion})` : ''}
                </span>
              </div>

              <div className="d-grid gap-xs" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(7rem, 1fr))' }}>
                <SummaryStat label="In bundle" value={preview.totalAssets} tone="muted" />
                <SummaryStat label="To import" value={preview.newAssets} tone="good" />
                <SummaryStat label="Already here" value={preview.duplicateAssets} tone="muted" />
                <SummaryStat label="Platform-linked" value={preview.platformLinkedAssets} tone="good" />
                <SummaryStat label="New authors" value={preview.newAuthors.length} />
              </div>

              <NameList label="Authors to create (disabled accounts)" names={preview.newAuthors} tone="new" />
              <NameList label="Authors matched locally" names={preview.matchedAuthors} tone="existing" />

              {preview.warnings.length > 0 && (
                <div className="bg-space bg-o-50 border-md border-amber rounded-sm p-xs d-flex flex-column gap-2xs">
                  {preview.warnings.map((warning) => (
                    <span key={warning} className="text-amber fs-sm">⚠ {warning}</span>
                  ))}
                </div>
              )}

              <label className="d-flex align-items-center gap-xs fs-sm">
                <input
                  className="checkbox"
                  type="checkbox"
                  checked={shareAll}
                  onChange={(e) => setShareAll(e.target.checked)}
                />
                <span>
                  Share every imported screenshot with all admins
                  <span className="text-slate-dark"> — otherwise each keeps the sharing setting it had in the source environment</span>
                </span>
              </label>

              <div className="d-flex gap-xs justify-content-end">
                <button className="btn btn--outline btn--slate" onClick={handleClose} disabled={stage === 'importing'}>
                  Cancel
                </button>
                <button
                  className="btn btn--fancy btn--green text-uppercase fw-bold"
                  disabled={stage === 'importing' || preview.newAssets === 0}
                  onClick={() => void handleCommit()}
                >
                  {stage === 'importing'
                    ? 'Importing…'
                    : preview.newAssets === 0
                      ? 'Nothing to import'
                      : `Import ${preview.newAssets} screenshot${preview.newAssets === 1 ? '' : 's'}`}
                </button>
              </div>
            </>
          )}

          {/* ── Result ──────────────────────────────────────────────────── */}
          {stage === 'result' && result && (
            <>
              <div className="d-grid gap-xs" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(7rem, 1fr))' }}>
                <SummaryStat label="Imported" value={result.imported} tone="good" />
                <SummaryStat label="Skipped" value={result.skipped} tone="muted" />
                <SummaryStat label="Authors created" value={result.authorsCreated.length} />
              </div>

              <NameList label="Authors created" names={result.authorsCreated} tone="new" />

              {result.thumbnailFailures > 0 && (
                <span className="text-amber fs-sm">
                  ⚠ {result.thumbnailFailures} thumbnail{result.thumbnailFailures === 1 ? '' : 's'} could not be
                  generated — those tiles fall back to the full-size image.
                </span>
              )}

              {result.warnings.length > 0 && (
                <div className="bg-space bg-o-50 border-md border-amber rounded-sm p-xs d-flex flex-column gap-2xs overflow-y-auto" style={{ maxHeight: '12rem' }}>
                  {result.warnings.map((warning) => (
                    <span key={warning} className="text-amber fs-sm">⚠ {warning}</span>
                  ))}
                </div>
              )}

              <div className="d-flex gap-xs justify-content-end">
                <button className="btn btn--fancy btn--green text-uppercase fw-bold" onClick={handleDone}>
                  Done
                </button>
              </div>
            </>
          )}
        </div>
      </Modal>
  );
}
